import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserCog, Loader2 } from 'lucide-react';

interface Props {
  value: string | null;
  onChange: (agentId: string | null) => void;
  label?: string;
  helpText?: string;
}

const UNASSIGNED = '__unassigned__';

/**
 * Lets an agency admin pick which agent inside their agency owns this listing.
 * New leads will be routed directly to that agent.
 *
 * Renders nothing if the current user is not an agency admin.
 */
export function AssignAgentSelector({
  value,
  onChange,
  label = 'Assign to Agent',
  helpText = 'New inquiries on this listing will be routed to this agent.',
}: Props) {
  const { user, hasRole } = useAuth();

  const { data: agency } = useQuery({
    queryKey: ['assign-agent-agency', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('agencies')
        .select('id, name')
        .eq('created_by', user!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user && hasRole('agency_admin'),
  });

  const { data: agents = [], isLoading } = useQuery({
    queryKey: ['assign-agent-list', agency?.id],
    queryFn: async () => {
      const { data: ap } = await supabase
        .from('agent_profiles')
        .select('user_id')
        .eq('agency_id', agency!.id);
      const ids = (ap || []).map(a => a.user_id);
      if (!ids.length) return [];
      const { data: profs } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url')
        .in('user_id', ids);
      return (profs || []).sort((a: any, b: any) => (a.full_name || '').localeCompare(b.full_name || ''));
    },
    enabled: !!agency?.id,
  });

  if (!hasRole('agency_admin')) return null;
  if (!agency) return null;

  return (
    <div className="rounded-xl border bg-card p-4 space-y-2">
      <div className="flex items-center gap-2">
        <UserCog className="h-4 w-4 text-primary" />
        <Label className="text-sm font-semibold">{label}</Label>
      </div>
      <p className="text-xs text-muted-foreground">{helpText}</p>

      {isLoading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading team…
        </div>
      ) : agents.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          No agents in your agency yet. Invite agents from Team Management.
        </p>
      ) : (
        <Select
          value={value || UNASSIGNED}
          onValueChange={(v) => onChange(v === UNASSIGNED ? null : v)}
        >
          <SelectTrigger><SelectValue placeholder="Select an agent" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={UNASSIGNED}>Unassigned (use agency default)</SelectItem>
            {agents.map((a: any) => (
              <SelectItem key={a.user_id} value={a.user_id}>{a.full_name || 'Agent'}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
